/**
 * Environment variable utilities
 * Supports local, dev, stage, and production environments
 */

export type Environment = 'local' | 'development' | 'staging' | 'production';

interface EnvConfig {
  // Environment Info
  ENVIRONMENT: Environment;
  IS_LOCAL: boolean;
  IS_DEVELOPMENT: boolean;
  IS_STAGING: boolean;
  IS_PRODUCTION: boolean;

  // API Authentication
  API_KEY: string;
  API_SECRET: string;
  API_SESSION_ID?: string;
  API_CSRF_TOKEN?: string;

  // API Configuration
  API_TIMEOUT: number;

  // API Service Endpoints
  API_CTRAC_ADMIN_RECRUITMENT_SERVICE_BASE_URL: string;
  API_SPHINX_SERVICE_BASE_URL: string;
  API_MYPROFILE_SERVICE_BASE_URL: string;
  API_PERSONAL_INFORMATION_SERVICE_BASE_URL: string;
  API_DOCUMENTS_SERVICE_BASE_URL: string;
  API_JOB_APPLICATIONS_SERVICE_BASE_URL: string;
  API_REFERENCE_SERVICE_BASE_URL: string;
  API_CREW_ASSIGNMENT_BASE_URL: string;
  CREW_JOURNEY_URL?: string;
  MYRCL_LOGIN_URL?: string;
  API_USER_PROFILE_SERVICE_BASE_URL: string;

  // External Links
  NEW_ADMIN_PAGE_URL?: string;
  MAINTENANCE_PAGE_URL?: string;
}

/**
 * Get environment variable with type safety
 * Checks runtime environment (window.__ENV__) first, then build-time environment
 */
export const getEnvVar = (
  key: string,
  required = false,
  fallback = ''
): string => {
  // Check runtime environment first (injected at container startup)
  const runtimeValue =
    typeof window !== 'undefined' && window.__ENV__?.[key];

  // Fall back to build-time environment
  const buildValue = import.meta.env[key];

  const value = runtimeValue || buildValue;

  if (required && !value) {
    throw new Error(`❌ Required environment variable ${key} is not set`);
  }

  return value || fallback;
};

/**
 * Determine the current environment
 */
const determineEnvironment = (): Environment => {
  const envValue = getEnvVar('VITE_ENVIRONMENT') || import.meta.env.MODE;

  switch (envValue) {
    case 'production':
    case 'prod':
      return 'production';
    case 'staging':
    case 'stage':
      return 'staging';
    case 'development':
    case 'dev':
      return 'development';
    case 'local':
    default:
      return 'local';
  }
};

/**
 * Validate required environment variables
 */
export const validateEnvironment = (environment: Environment): void => {
  const requiredVars: string[] = [];

  // Require API credentials for non-local environments
  if (environment !== 'local') {
    requiredVars.push('VITE_API_KEY', 'VITE_API_SECRET');
  }

  // Additional validation for staging and production
  if (environment === 'staging' || environment === 'production') {
    requiredVars.push(
      'VITE_API_CTRAC_ADMIN_RECRUITMENT_SERVICE_BASE_URL',
      'VITE_API_MYPROFILE_SERVICE_BASE_URL',
      'VITE_API_PERSONAL_INFORMATION_SERVICE_BASE_URL',
      'VITE_API_JOB_APPLICATIONS_SERVICE_BASE_URL',
      'VITE_API_REFERENCE_SERVICE_BASE_URL'
    );
  }

  const missing = requiredVars.filter((varName) => !getEnvVar(varName));

  if (missing.length > 0) {
    throw new Error(
      `❌ Missing required environment variables for ${environment}:\n${missing.map((v) => `  - ${v}`).join('\n')}`
    );
  }
};

/**
 * Get validated environment configuration
 */
export const getEnvConfig = (): EnvConfig => {
  const environment = determineEnvironment();
  const isLocal = environment === 'local';
  const isDevelopment = environment === 'development';
  const isStaging = environment === 'staging';
  const isProduction = environment === 'production';

  // Validate environment variables
  try {
    validateEnvironment(environment);
  } catch (error) {
    if (isProduction) {
      throw error; // Fail fast in production
    }
    console.warn(error); // Warn in development/staging/local
  }

  return {
    // Environment Info
    ENVIRONMENT: environment,
    IS_LOCAL: isLocal,
    IS_DEVELOPMENT: isDevelopment,
    IS_STAGING: isStaging,
    IS_PRODUCTION: isProduction,

    // API Authentication
    API_KEY: getEnvVar('VITE_API_KEY', false, ''),
    API_SECRET: getEnvVar('VITE_API_SECRET', false, ''),
    API_SESSION_ID: getEnvVar('VITE_API_SESSION_ID'),
    API_CSRF_TOKEN: getEnvVar('VITE_API_CSRF_TOKEN'),
    // API Configuration
    API_TIMEOUT: parseInt(getEnvVar('VITE_API_TIMEOUT', false, '30000')),

    // API Service Endpoints
    API_CTRAC_ADMIN_RECRUITMENT_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_CTRAC_ADMIN_RECRUITMENT_SERVICE_BASE_URL',
      false,
      'http://localhost:8098/api/v1/ctrac/admin/recruitment-service'
    ),
    API_SPHINX_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_SPHINX_SERVICE_BASE_URL',
      false,
      'http://localhost:4000/sphinx'
    ),
    API_MYPROFILE_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_MYPROFILE_SERVICE_BASE_URL',
      false,
      'http://localhost:8185/api/v1/ctrac-bff-myprofile-service'
    ),
    API_PERSONAL_INFORMATION_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_PERSONAL_INFORMATION_SERVICE_BASE_URL',
      false,
      'http://localhost:8186/api/v1/ctrac-personal-info-bff-service'
    ),
    API_DOCUMENTS_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_DOCUMENTS_SERVICE_BASE_URL',
      false,
      'http://localhost:8081/api/v1/ctrac/document/travel-documents'
    ),
    API_JOB_APPLICATIONS_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_JOB_APPLICATIONS_SERVICE_BASE_URL',
      false,
      'http://localhost:8182/api/v1/ctrac-services'
    ),
    API_REFERENCE_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_REFERENCE_SERVICE_BASE_URL',
      false,
      'http://localhost:8187/api/v1/reference-service'
    ),
    API_CREW_ASSIGNMENT_BASE_URL: getEnvVar(
      'VITE_API_CREW_ASSIGNMENT_BASE_URL',
      false,
      'https://api.stg-ithr.rccl.com/api/v1/crewassignment'
    ), 
    API_USER_PROFILE_SERVICE_BASE_URL: getEnvVar(
      'VITE_API_USER_PROFILE_SERVICE_BASE_URL',
      false,
      'https://api.dev-ithr.rccl.com/api/v1/user-profile-service'
    ),
    CREW_JOURNEY_URL: getEnvVar('VITE_CREW_JOURNEY_URL', false, ''),
    MYRCL_LOGIN_URL: getEnvVar('VITE_MYRCL_LOGIN_URL', false, ''),
    MAINTENANCE_PAGE_URL: getEnvVar('VITE_LINK_MAINTENANCE_PAGE', false, ''),
    NEW_ADMIN_PAGE_URL: getEnvVar('VITE_LINK_NEW_ADMIN_PAGE', false, ''),
  };
};

/**
 * Log environment configuration (excluding sensitive data)
 */
export const logEnvConfig = (): void => {
  const config = getEnvConfig();

  console.group('🔧 Environment Configuration');
  console.log('Environment:', config.ENVIRONMENT);
  console.log('Local:', config.IS_LOCAL);
  console.log('Development:', config.IS_DEVELOPMENT);
  console.log('Staging:', config.IS_STAGING);
  console.log('Production:', config.IS_PRODUCTION);
  console.log('');
  console.log('API Configuration:');
  console.log('  - Timeout:', config.API_TIMEOUT, 'ms');
  console.log('  - Has API Key:', !!config.API_KEY);
  console.log('  - Has API Secret:', !!config.API_SECRET);
  console.log('  - Has Session ID:', !!config.API_SESSION_ID);
  console.log('');
  console.log('API Endpoints:');
  console.log('  - CTRAC Admin Recruitment:', config.API_CTRAC_ADMIN_RECRUITMENT_SERVICE_BASE_URL);
  console.log('  - Sphinx:', config.API_SPHINX_SERVICE_BASE_URL);
  console.log('  - My Profile:', config.API_MYPROFILE_SERVICE_BASE_URL);
  console.log('  - Personal Info:', config.API_PERSONAL_INFORMATION_SERVICE_BASE_URL);
  console.log('  - Documents:', config.API_DOCUMENTS_SERVICE_BASE_URL);
  console.log('  - Job Applications:', config.API_JOB_APPLICATIONS_SERVICE_BASE_URL);
  console.log('  - Reference:', config.API_REFERENCE_SERVICE_BASE_URL);
  console.log('  - Crew Assignment:', config.API_CREW_ASSIGNMENT_BASE_URL);
  console.log('  - User Profile:', config.API_USER_PROFILE_SERVICE_BASE_URL);
  console.groupEnd();

  if (config.IS_LOCAL) {
    console.info('🏠 Running in LOCAL mode');
  } else if (config.IS_DEVELOPMENT) {
    console.info('ℹ️ Running in DEVELOPMENT mode');
  } else if (config.IS_STAGING) {
    console.info('⚠️ Running in STAGING mode');
  } else if (config.IS_PRODUCTION) {
    console.info('🚀 Running in PRODUCTION mode');
  }
};

export default getEnvConfig;